import { useStore } from '@/hooks/useStore';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { Settings } from 'lucide-react';
import { t } from 'i18next';
import QRIcon from '@/assets/images/pngs/qr.png';
import notePadIcon from '@/assets/images/pngs/Notepad.png';
import noteBookIcon from '@/assets/images/pngs/Notebook.png';
import shieldIcon from '@/assets/images/pngs/ShieldWarning.png';
import questionIcon from '@/assets/images/pngs/Question.png';
import logoutIcon from '@/assets/images/pngs/SignOut.png';
import Header from '@/components/layout/Header';
import { useNavigate } from 'react-router-dom';
import { ROUTES, TYPE_ACCOUNT } from '@/configs/constants';
import pencilHeaderIcon from '@/assets/images/pngs/PencilLine.png';
import { handleLongText } from '@/utils/utils';
import { toastify } from '@/utils/toastify';

declare global {
    interface WindowEventMap {
        beforeinstallprompt: BeforeInstallPromptEvent
    }
}

interface BeforeInstallPromptEvent extends Event {
    prompt: () => Promise<void>,
    userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

export default observer(function ProfilePage() {
    const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
    const { modalStore: { showModal, hideModal } } = useStore();
    useEffect(() => {
        const handleBeforeInstallPrompt = (event: Event) => {
            event.preventDefault();
            setDeferredPrompt(event as BeforeInstallPromptEvent);
        };

        window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
        return () => {
            window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
        };
    }, []);

    // Lắng nghe sự kiện appinstalled
    useEffect(() => {
        const handleAppInstalled = () => {
            localStorage.setItem('isAppInstalled', 'true');
            toastify('success', 'インストールが完了しました。アプリ画面で確認してください。');
        };

        window.addEventListener('appinstalled', handleAppInstalled);

        return () => {
            window.removeEventListener('appinstalled', handleAppInstalled);
        };
    }, []);

    // store
    const {
        authStore: { logout, profile }
    } = useStore();
    const navigate = useNavigate();

    const goBack = () => {
        navigate(ROUTES.home.href);
    };
    const gotoEditProfile = () => {
        navigate(ROUTES.editProfile.href);
    };

    const gotoQRProfile = () => {
        navigate(ROUTES.qrProfile.href);
    };

    const gotoTermsOfService = () => {
        navigate(ROUTES.termsOfService.href);
    };

    const gotoPrivacyPolicy = () => {
        navigate(ROUTES.privacyPolicy.href);
    };

    // const gotoHowToUse = () => {
    //     navigate(ROUTES.howToUse.href);
    // };

    // const gotoFaq = () => {
    //     navigate(ROUTES.faq.href);
    // };

    const getIdSocial = (profile) => {
        const type = profile?.socialsAuth[0]?.type;
        if (!profile || !type) {
            return '';
        }
        const data = profile.socialsAuth[0];
        switch (type) {
            case TYPE_ACCOUNT.GOOGLE:
                return data.email || '';
            case TYPE_ACCOUNT.PASSKEY:
                return data.email || '';
            case TYPE_ACCOUNT.TWITTER:
                return data.name || data.twitterId || '';
            case TYPE_ACCOUNT.LINE:
                return data.name || data.lineSubId || '';
            default:
                break;
        }
    };

    const handleInstall = async () => {
        const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent.toLowerCase());
        const isDesktopChrome = /chrome/i.test(navigator.userAgent.toLowerCase()) && !/mobile/i.test(navigator.userAgent.toLowerCase());
        const isAndroidChrome = /chrome/i.test(navigator.userAgent.toLowerCase()) && /mobile/i.test(navigator.userAgent.toLowerCase());
        const isOtherDesktop = !isDesktopChrome && !/mobile/i.test(navigator.userAgent.toLowerCase());
        const isUnsupportedBrowser = !isIOS && !isDesktopChrome && !isAndroidChrome && !isOtherDesktop;
        console.log('isDesktopChrome', isDesktopChrome);
        // Kiểm tra chế độ display và trạng thái cài đặt
        const isStandalone = window.matchMedia('(display-mode: standalone)').matches ||
            window.matchMedia('(display-mode: minimal-ui)').matches;
        const isAppInstalled = localStorage.getItem('isAppInstalled') === 'true';

        // Nếu PWA đã được cài đặt
        if ((isStandalone || isAppInstalled) && !isIOS && !isDesktopChrome) {
            toastify('success', 'インストールが完了しました。アプリ画面で確認してください。');
            return;
        }

        // Trường hợp iOS
        if (isIOS) {
            showModal({
                id: 'install-guide',
                showCloseIcon: false,
                saveButton: 'OK',
                cancelButton: false,
                content: (
                    <div>
                        <p>iOSでは自動的にインストールできません。</p>
                        <p>
                            Safariの
                            <strong>共有ボタン</strong>
                            {' '}
                            をタップし、
                            <strong>「ホーム画面に追加」</strong>
                            {' '}
                            を選んでください。
                        </p>
                        <img src='/pwa-install-ios.png' alt='iOS install guide' style={{ width: '100%', marginTop: '1em' }} />
                    </div>
                ),
                onSave: () => {
                    hideModal('install-guide');
                    localStorage.setItem('isAppInstalled', 'true');
                }
            });
            return;
        }

        // Trường hợp Chrome desktop
        if (isDesktopChrome) {
            showModal({
                id: 'install-guide',
                showCloseIcon: false,
                saveButton: 'OK',
                cancelButton: false,
                content: (
                    <div>
                        <p>Chromeでは自動的にインストールできません。</p>
                        <p>
                            右上の
                            <strong>メニュー（3つのドット）</strong>
                            {' '}
                            をクリックし、
                            <strong>「アプリをインストール」</strong>
                            {' '}
                            を選んでください。
                        </p>
                        <img src='/pwa-install-chrome.png' alt='Chrome install guide' style={{ width: '100%', marginTop: '1em' }} />
                    </div>
                ),
                onSave: () => {
                    hideModal('install-guide');
                }
            });
            return;
        }

        // Trường hợp Android Chrome
        if (isAndroidChrome) {
            if (deferredPrompt) {
                await deferredPrompt.prompt();
                const { outcome } = await deferredPrompt.userChoice;
                if (outcome === 'accepted') {
                    localStorage.setItem('isAppInstalled', 'true');
                } else {
                    toastify('error', 'インストールがキャンセルされました。');
                }
                setDeferredPrompt(null);
            } else {
                toastify('info', 'アプリはすでにインストールされているか、インストールがサポートされていません。');
            }
            return;
        }

        // Trường hợp PC khác (ngoài Chrome desktop)
        if (isOtherDesktop) {
            showModal({
                id: 'install-guide-unsupported',
                showCloseIcon: false,
                saveButton: 'OK',
                cancelButton: false,
                content: (
                    <div>
                        <p>このブラウザではPWAのインストールがサポートされていません。</p>
                        <p>
                            PWAをインストールするには、
                            <strong>Google Chrome</strong>
                            {' '}
                            をご利用ください。
                        </p>
                    </div>
                ),
                onSave: () => {
                    hideModal('install-guide-unsupported');
                }
            });
            return;
        }

        // Trường hợp các thiết bị/trình duyệt không xác định
        if (isUnsupportedBrowser) {
            toastify('info', 'このデバイスまたはブラウザではPWAのインストールがサポートされていません。Google Chromeをご利用ください。');
            return;
        }
    };

    return (
        <div className='w-screen h-screen flex flex-col justify-start pt-[72px]'>
            <Header goBackFunction={goBack} title={t('text.myAccount')} iconRight={pencilHeaderIcon} iconRightFunction={gotoEditProfile} />
            <div className='px-[16px] mt-[24px]'>
                {/* <div className='w-full flex justify-center items-center'>
                    <div className='w-[80px] h-[80px] rounded-full relative'>
                        <img src={avatarDefault} alt='avatar' className='w-[80px] h-[80px]' />
                        <div onClick={gotoEditProfile} className='bg-white w-[20px] h-[20px] absolute rounded-full right-0 bottom-0 flex justify-center items-center'>
                            <img src={pencilIcon} alt='edit' className='w-[13px] h-[13px]' />
                        </div>
                    </div>

                </div> */}
                <p className='text-[24px] font-normal text-center text-black mt-[12px] whitespace-nowrap'>{handleLongText(profile?.username, 7)}</p>
                <p className='text-[18px] font-light text-center text-text mt-8[px]'>{getIdSocial(profile)}</p>
                <div className='mt-[24px] space-y-[4px]'>
                    {/* <button onClick={gotoQRProfile} className='flex items-center bg-white h-[48px] px-[16px] rounded-t-[20px] rounded-b-[4px] w-full shadow-lg shadow-shadow'>
                        <img src={QRIcon} alt='qr-icon' className='w-[24px] h-[24px]' />
                        <span className='text-gray text-[15px] ml-[16px]'>{t('text.myQr')}</span>
                    </button> */}
                    <a href='https://note.com/omikuji_furufuru/n/n050956c2785a' target='_blank' className='flex items-center bg-white h-[48px] px-[16px] rounded-[4px] w-full shadow-lg shadow-shadow' rel='noreferrer'>
                        <img src={noteBookIcon} alt='note-book' className='w-[24px] h-[24px]' />
                        <span className='text-gray text-[15px] ml-[16px]'>{t('text.howToUse')}</span>
                    </a>
                    <button onClick={gotoTermsOfService} className='flex items-center bg-white h-[48px] px-[16px] rounded-[4px] w-full shadow-lg shadow-shadow' rel='noreferrer'>
                        <img src={notePadIcon} alt='note-pad' className='w-[24px] h-[24px]' />
                        <span className='text-gray text-[15px] ml-[16px]'>{t('text.termsOfUser')}</span>
                    </button>
                    <button onClick={gotoPrivacyPolicy} className='flex items-center bg-white h-[48px] px-[16px] rounded-[4px] w-full shadow-lg shadow-shadow' rel='noreferrer'>
                        <img src={shieldIcon} alt='shield' className='w-[24px] h-[24px]' />
                        <span className='text-gray text-[15px] ml-[16px]'>{t('text.privacyPolicy')}</span>
                    </button>
                    <a href='https://note.com/omikuji_furufuru/n/nabd49122513e' target='_blank' className='flex items-center bg-white h-[48px] px-[16px] rounded-[4px] w-full shadow-lg shadow-shadow' rel='noreferrer'>
                        <img src={questionIcon} alt='question' className='w-[24px] h-[24px]' />
                        <span className='text-gray text-[15px] ml-[16px]'>{t('text.faq')}</span>
                    </a>

                    <button onClick={handleInstall} className='flex items-center bg-white h-[48px] px-[16px] rounded-[4px] w-full shadow-lg shadow-shadow' rel='noreferrer'>
                        <Settings className='w-[24px] h-[24px] text-gray' />
                        <span className='text-gray text-[15px] ml-[16px]'>{t('text.setting_pwa')}</span>
                    </button>
                    <button onClick={logout} className='flex items-center bg-white h-[48px] px-[16px] rounded-b-[20px] rounded-t-[4px] w-full shadow-lg shadow-shadow'>
                        <img src={logoutIcon} alt='logout' className='w-[24px] h-[24px]' />
                        <span className='text-gray text-[15px] ml-[16px]'>{t('text.logout')}</span>
                    </button>
                </div>
            </div>

        </div>
    );
});
